import { useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import { extractRelevantHeaders } from '../constants/apiHeaders';
import { ApiClientResponse } from './apiClientResponse';
import { HttpResponseType } from '../constants/httpResponseType';
import { ApiError } from './apiError';
import { createApiClient } from './createApiClient';
import { DEFAULT_CLIENT_OPTIONS } from './defaults';

/**
 * Infrastructure hook that provides a consistent authenticated API client.
 *
 * Feature components should generally use specialized API hooks, not this hook directly.
 *
 * Features:
 * - Request cancellation support via AbortController
 * - Automatic retry on network failures (using axios-retry)
 * - Consistent response format using ApiClientResponse
 * - Proper error handling with ApiError
 */
export function useApiClient(clientOptions = DEFAULT_CLIENT_OPTIONS) {
	const sharedAbortControllerRef = useRef(new AbortController());

	const apiClient = useMemo(
		() => createApiClient(clientOptions),
		[clientOptions]
	);

	/**
	 * Handles errors from Axios requests and converts them into consistent ApiClientResponse format.
	 * Supports both cancelled requests and standard HTTP errors.
	 */
	const handleError = useCallback((error) => {
		if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
			return new ApiClientResponse({
				isAborted: true,
				isError: false,
				isMultiStatus: false,
				data: null,
				status: 0,
				error: null,
				eTag: null,
				headers: null,
			});
		}

		const { data, status, statusText, headers } = error?.response ?? {};

		const apiError = new ApiError({
			status: data?.status ?? status,
			message: data?.errorMessage ?? statusText ?? 'An error occurred',
			errorCode: data?.errorCode,
			moreInfo: data?.moreInfo,
			validationErrors: data?.validationErrors,
		});

		return new ApiClientResponse({
			isAborted: false,
			isError: true,
			isMultiStatus: false,
			data: null,
			status,
			error: apiError,
			eTag: headers?.etag ?? null,
			headers: extractRelevantHeaders(headers),
		});
	}, []);

	/**
	 * Normalizes successful HTTP responses into ApiClientResponse.
	 */
	const createSuccessResponse = useCallback(({ data, status, headers }) => {
		const relevantHeaders = extractRelevantHeaders(headers);
		const isMultiStatus = status === StatusCodes.MULTI_STATUS;

		return new ApiClientResponse({
			isAborted: false,
			isError: false,
			isMultiStatus,
			data,
			status,
			error: null,
			eTag: headers?.etag ?? null,
			headers: relevantHeaders,
		});
	}, []);

	/** Cancels the current shared request and resets the AbortController */
	const cancel = useCallback(() => {
		sharedAbortControllerRef.current.abort();
		sharedAbortControllerRef.current = new AbortController();
	}, []);

	/** Resolves which AbortController to use (passed one or shared) */
	const resolveAbortController = useCallback((controller) => {
		if (controller instanceof AbortController && !controller.signal.aborted) {
			return controller;
		}

		const shared = sharedAbortControllerRef.current;

		if (shared.signal.aborted) {
			sharedAbortControllerRef.current = new AbortController();
		}

		return sharedAbortControllerRef.current;
	}, []);

	/**
	 * GET request
	 * @param {string} url - Relative endpoint URL
	 * @param {AbortController} [abortController] - Optional abort controller
	 * @returns {Promise<ApiClientResponse>} Standardized response
	 */
	const get = useCallback(
		async (url, abortController = null) => {
			try {
				const controller = resolveAbortController(abortController);
				const { data, status, headers } = await apiClient.get(url, {
					signal: controller.signal,
				});

				return createSuccessResponse({ data, status, headers });
			} catch (error) {
				return handleError(error);
			}
		},
		[createSuccessResponse, handleError, apiClient, resolveAbortController]
	);

	/**
	 * GET request that expects a Blob response (useful for file downloads)
	 * @param {string} url - Relative endpoint URL
	 * @param {AbortController} [abortController] - Optional abort controller
	 * @returns {Promise<ApiClientResponse<Blob>>}
	 */
	const getBlob = useCallback(
		async (url, abortController = null) => {
			try {
				const controller = resolveAbortController(abortController);
				const { data, status, headers } = await apiClient.get(url, {
					signal: controller.signal,
					responseType: HttpResponseType.Blob,
				});

				return createSuccessResponse({ data, status, headers });
			} catch (error) {
				return handleError(error);
			}
		},
		[createSuccessResponse, handleError, apiClient, resolveAbortController]
	);

	/**
	 * POST request
	 * @param {string} url - Relative endpoint URL
	 * @param {any} [payload] - Request body
	 * @param {Object} [options] - Additional options
	 * @param {'json' | 'blob' | 'arraybuffer' | 'text'} [options.responseType]
	 * @param {AbortController} [abortController] - Optional abort controller
	 * @returns {Promise<ApiClientResponse>} Standardized response
	 */
	const post = useCallback(
		async (url, payload = null, options = {}, abortController = null) => {
			try {
				const controller = resolveAbortController(abortController);
				const responseType = options?.responseType ?? HttpResponseType.Json;

				const { data, status, headers } = await apiClient.post(url, payload, {
					signal: controller.signal,
					responseType,
				});

				return createSuccessResponse({ data, status, headers });
			} catch (error) {
				return handleError(error);
			}
		},
		[createSuccessResponse, handleError, apiClient, resolveAbortController]
	);

	/**
	 * PUT request
	 * @param {string} url - Relative endpoint URL
	 * @param {any} [payload] - Request body
	 * @param {AbortController} [abortController] - Optional abort controller
	 * @returns {Promise<ApiClientResponse>}
	 */
	const put = useCallback(
		async (url, payload = null, abortController = null) => {
			try {
				const controller = resolveAbortController(abortController);
				const { data, status, headers } = await apiClient.put(url, payload, {
					signal: controller.signal,
				});

				return createSuccessResponse({ data, status, headers });
			} catch (error) {
				return handleError(error);
			}
		},
		[createSuccessResponse, handleError, apiClient, resolveAbortController]
	);

	/**
	 * PATCH request
	 * @param {string} url - Relative endpoint URL
	 * @param {any} payload - Request body
	 * @param {AbortController} [abortController] - Optional abort controller
	 * @returns {Promise<ApiClientResponse>}
	 */
	const patch = useCallback(
		async (url, payload, abortController = null) => {
			try {
				const controller = resolveAbortController(abortController);
				const { data, status, headers } = await apiClient.patch(url, payload, {
					signal: controller.signal,
				});

				return createSuccessResponse({ data, status, headers });
			} catch (error) {
				return handleError(error);
			}
		},
		[createSuccessResponse, handleError, apiClient, resolveAbortController]
	);

	/**
	 * DELETE request
	 * @param {string} url - Relative endpoint URL
	 * @param {AbortController} [abortController] - Optional abort controller
	 * @returns {Promise<ApiClientResponse>}
	 */
	const del = useCallback(
		async (url, abortController = null) => {
			try {
				const controller = resolveAbortController(abortController);
				const { data, status, headers } = await apiClient.delete(url, {
					signal: controller.signal,
				});

				return createSuccessResponse({ data, status, headers });
			} catch (error) {
				return handleError(error);
			}
		},
		[createSuccessResponse, handleError, apiClient, resolveAbortController]
	);

	/**
	 * POST request that expects a Blob response (useful for file downloads)
	 * @param {string} url - Relative endpoint URL
	 * @param {any} [payload] - Request body
	 * @param {AbortController} [abortController] - Optional abort controller
	 * @returns {Promise<ApiClientResponse<Blob>>}
	 */
	const postBlob = useCallback(
		async (url, payload = null, abortController = null) =>
			post(
				url,
				payload,
				{ responseType: HttpResponseType.Blob },
				abortController
			),
		[post]
	);

	const api = useMemo(
		() => ({
			get,
			getBlob,
			post,
			put,
			patch,
			del,
			postBlob,
			cancel,
		}),
		[get, getBlob, post, put, patch, del, postBlob, cancel]
	);

	return api;
}
